import React, { useEffect, useState } from 'react';
import { useCompetition } from '../../contexts/CompetitionContext';
import axios from 'axios';
import {
  CheckCircle, Loader2, Play, Pause, Timer, SkipForward,
  RotateCcw, RefreshCw, Megaphone, Send, UserMinus, Users, Clock, AlertCircle, Mail,
  Link2, Unlink, FolderOpen, Trash2, Copy, Radio, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Small self-contained card that surfaces the permanent shareable
 * /live-leaderboard/{link_id} URL for a competition. Auto-generated on setup.
 * Admin can post the URL on Facebook/WhatsApp days before the event to build hype;
 * the same link switches to the live leaderboard once the event goes live.
 */
const ShareableLiveLinkCard = ({ competition }) => {
  const [copied, setCopied] = useState(false);
  const linkId = competition?.live_link_id;
  if (!linkId) return null;
  const url = `${window.location.origin}/live-leaderboard/${linkId}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: focus + select the input so user can Ctrl+C manually
      const el = document.getElementById('live-link-input');
      if (el) { el.focus(); el.select(); }
    }
  };

  return (
    <div className="card-olympiad p-6" data-testid="shareable-live-link-card">
      <div className="flex items-center gap-2 mb-2">
        <Radio className="w-5 h-5 text-[#ef4444]" />
        <h2 className="text-lg font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Shareable Live Leaderboard Link
        </h2>
      </div>
      <p className="text-sm text-[#64748b] mb-4">
        Post this link on Facebook, WhatsApp, or anywhere. Before the event it shows a countdown; during the event it becomes a live leaderboard; after the event it shows final results & winner. <strong>Same link, three states.</strong>
      </p>
      <div className="flex items-center gap-2">
        <input
          id="live-link-input"
          type="text"
          value={url}
          readOnly
          onFocus={(e) => e.target.select()}
          className="flex-1 px-4 py-3 rounded-xl border-2 border-[#e2e8f0] bg-[#f8fafc] font-mono text-sm text-[#0f172a] focus:outline-none focus:border-[#2565eb]"
          data-testid="shareable-live-link-input"
        />
        <button
          onClick={copy}
          className="btn-secondary flex items-center gap-2 flex-shrink-0"
          data-testid="shareable-live-link-copy-btn"
        >
          {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary flex items-center gap-2 flex-shrink-0"
          data-testid="shareable-live-link-open-btn"
          title="Open in new tab"
        >
          <ExternalLink className="w-4 h-4" />
          Preview
        </a>
      </div>
    </div>
  );
};

const AdminCompetitionPage = () => {
  const { stage, refreshState, competition } = useCompetition();
  const [activeSection, setActiveSection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Setup form state
  const [setupForm, setSetupForm] = useState({
    name: '',
    eventDate: '',
    eventStartTime: '',
    venue: '',
    seatLimit: 50,
    participationFee: 10,
    ageGroup: ''
  });
  const [setupComplete, setSetupComplete] = useState(false);
  const [qbStatus, setQbStatus] = useState({ folders: [], linked_id: '' });

  // Registration state
  const [regOpen, setRegOpen] = useState(false);
  const [regStats, setRegStats] = useState({ total: 0, confirmed: 0, remaining: 0 });

  // Event Control state
  const [competitionStatus, setCompetitionStatus] = useState(null);
  const [lobbyCount, setLobbyCount] = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementText, setAnnouncementText] = useState('');
  const [reminderStatus, setReminderStatus] = useState({ sent: false, count: 0 });
  const [sendingReminder, setSendingReminder] = useState(false);

  // Inline confirmation state
  const [confirmAction, setConfirmAction] = useState(null); // { key, message, onConfirm }

  useEffect(() => {
    loadCompetitionData();
    // Poll competition status every 3 seconds for live updates
    const interval = setInterval(async () => {
      try {
        const statusRes = await axios.get(`${API_URL}/api/competition/status`, { withCredentials: true });
        setCompetitionStatus(statusRes.data);
      } catch (e) {}
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const loadCompetitionData = async () => {
    setLoading(true);
    try {
      const [compRes, regRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/competition-state`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/registrations`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);

      // Capture the just-fetched seatLimit so we can compute regStats.remaining without relying
      // on stale setupForm state (setSetupForm above is async and hasn't applied yet).
      let effectiveSeatLimit = null;
      if (compRes.data?.competition) {
        const comp = compRes.data.competition;
        // Convert stored "10:00 AM" format to "10:00" for HTML time input
        let timeVal = comp.eventStartTime || '';
        if (timeVal && timeVal.match(/\d{1,2}:\d{2}\s*(AM|PM)/i)) {
          const [timePart, period] = timeVal.split(/\s+/);
          let [h, m] = timePart.split(':').map(Number);
          if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
          if (period.toUpperCase() === 'AM' && h === 12) h = 0;
          timeVal = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        }
        const sl = parseInt(comp.seatLimit, 10);
        effectiveSeatLimit = sl > 0 ? sl : null;
        setSetupForm({
          name: comp.name || '',
          eventDate: comp.eventDate || '',
          eventStartTime: timeVal,
          venue: comp.venue || '',
          seatLimit: effectiveSeatLimit || '',
          participationFee: comp.participationFee || 10,
          ageGroup: comp.ageGroup || ''
        });
        setSetupComplete(compRes.data.stage !== 'setup');
        setRegOpen(compRes.data.stage === 'registration');
        
        if (compRes.data.stage === 'live' || compRes.data.stage === 'complete') {
          setActiveSection(3);
        } else if (compRes.data.stage === 'registration' || compRes.data.stage === 'registration_closed') {
          setActiveSection(2);
        } else if (compRes.data.stage === 'setup_complete') {
          setActiveSection(2);
        }
      }

      const regs = regRes.data || [];
      const paidCount = regs.filter(r => r.payment_status === 'paid').length;
      // Use the freshly-fetched seatLimit (not stale setupForm.seatLimit state).
      const limitForCalc = effectiveSeatLimit != null ? effectiveSeatLimit : 0;
      setRegStats({
        total: regs.length,
        confirmed: paidCount,
        remaining: Math.max(0, limitForCalc - paidCount)
      });

      // Load competition status
      try {
        const statusRes = await axios.get(`${API_URL}/api/competition/status`, { withCredentials: true });
        setCompetitionStatus(statusRes.data);
      } catch (e) {}

      // Load lobby
      try {
        const lobbyRes = await axios.get(`${API_URL}/api/competition/lobby-status`, { withCredentials: true });
        setLobbyCount(lobbyRes.data?.participant_count ?? lobbyRes.data?.participants?.length ?? 0);
      } catch (e) {}

      // Load announcements
      try {
        const annRes = await axios.get(`${API_URL}/api/admin/announcements`, { withCredentials: true });
        setAnnouncements(annRes.data.announcements || []);
      } catch (e) {}

      // Load question bank status
      try {
        const qbRes = await axios.get(`${API_URL}/api/admin/question-bank-status`, { withCredentials: true });
        setQbStatus(qbRes.data);
      } catch (e) {}

      // Load reminder status
      try {
        const remRes = await axios.get(`${API_URL}/api/admin/reminder-status`, { withCredentials: true });
        setReminderStatus(remRes.data);
      } catch (e) {}

    } catch (e) {
      setError('Failed to load competition data');
    } finally {
      setLoading(false);
    }
  };

  const saveSetup = async () => {
    setLoading(true);
    setError('');
    try {
      // Convert 24h time to 12h AM/PM for storage/display
      const payload = { ...setupForm };
      // Coerce seatLimit to a valid integer — never let empty/NaN reach the backend.
      const sl = parseInt(payload.seatLimit, 10);
      if (!sl || sl < 1) {
        setError('Seat Limit must be at least 1');
        setLoading(false);
        return;
      }
      payload.seatLimit = sl;
      if (payload.eventStartTime && payload.eventStartTime.match(/^\d{2}:\d{2}$/)) {
        let [h, m] = payload.eventStartTime.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        if (h > 12) h -= 12;
        if (h === 0) h = 12;
        payload.eventStartTime = `${h}:${String(m).padStart(2, '0')} ${period}`;
      }
      await axios.post(`${API_URL}/api/admin/competition-setup`, payload, { withCredentials: true });
      setSetupComplete(true);
      setSuccess('Setup saved! You can now proceed to Registration.');
      refreshState();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save setup');
    } finally {
      setLoading(false);
    }
  };

  const linkQuestionBank = async (folderId) => {
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/link-question-bank`, { folder_id: folderId }, { withCredentials: true });
      setQbStatus(prev => ({
        ...prev,
        linked_id: folderId,
        folders: prev.folders.map(f => ({ ...f, is_linked: f.id === folderId }))
      }));
      setSuccess('Question bank linked to competition.');
      setConfirmAction(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to link');
      setConfirmAction(null);
    }
  };

  const unlinkQuestionBank = async () => {
    setError('');
    try {
      await axios.post(`${API_URL}/api/admin/unlink-question-bank`, {}, { withCredentials: true });
      setQbStatus(prev => ({
        ...prev,
        linked_id: '',
        folders: prev.folders.map(f => ({ ...f, is_linked: false }))
      }));
      setSuccess('Question bank unlinked.');
      setConfirmAction(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unlink');
      setConfirmAction(null);
    }
  };

  const deleteCompetition = async () => {
    setLoading(true);
    setError('');
    try {
      await axios.delete(`${API_URL}/api/admin/competition`, { withCredentials: true });
      setSuccess('Competition archived. You can now create a new one.');
      setConfirmAction(null);
      // Reset local state to blank setup
      setSetupForm({ name: '', eventDate: '', eventStartTime: '', venue: '', seatLimit: 50, participationFee: 10, ageGroup: '' });
      setSetupComplete(false);
      setRegOpen(false);
      setRegStats({ total: 0, confirmed: 0, remaining: 0 });
      setCompetitionStatus(null);
      setLobbyCount(0);
      setAnnouncements([]);
      setQbStatus({ folders: [], linked_id: '' });
      setActiveSection(1);
      refreshState();
      // Reload everything from server
      await loadCompetitionData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete competition');
      setConfirmAction(null);
    } finally {
      setLoading(false);
    }
  };

  const openRegistration = async () => {
    setLoading(true);
    setError('');
    setConfirmAction(null);
    try {
      await axios.post(`${API_URL}/api/admin/open-registration`, {}, { withCredentials: true });
      setRegOpen(true);
      setSuccess('Registration is now open!');
      refreshState();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to open registration');
    } finally {
      setLoading(false);
    }
  };

  const closeRegistration = async () => {
    setLoading(true);
    setError('');
    setConfirmAction(null);
    try {
      await axios.post(`${API_URL}/api/admin/close-registration`, {}, { withCredentials: true });
      setRegOpen(false);
      setSuccess('Registration closed. You can now start the competition.');
      refreshState();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to close registration');
    } finally {
      setLoading(false);
    }
  };

  const startCompetition = async () => {
    setLoading(true);
    setError('');
    setConfirmAction(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/start-competition`, { countdown: 3 }, { withCredentials: true });
      setSuccess(`Competition starting! Round 1 begins in ${res.data.countdown_seconds}s...`);
      refreshState();
      // Do NOT setTimeout-reload here — backend grace is 60s (R1_GRACE_SECONDS),
      // not the 3–4s used previously. The 3-second poll already running in the
      // useEffect above will pick up status transitions (inactive → grace_r1 →
      // round_1) naturally as they happen. Clearing success message after 8s
      // is enough for admin to read the toast.
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start competition');
    } finally {
      setLoading(false);
    }
  };

  const pauseCompetition = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/pause-competition`, {}, { withCredentials: true });
      loadCompetitionData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to pause');
    }
  };

  const resumeCompetition = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/resume-competition`, {}, { withCredentials: true });
      loadCompetitionData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resume');
    }
  };

  const extendTime = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/extend-time`, {}, { withCredentials: true });
      setSuccess('+2 minutes added');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to extend time');
    }
  };

  const endRound = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/end-round`, {}, { withCredentials: true });
      loadCompetitionData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to end round');
    }
  };

  const nextRound = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/next-round`, {}, { withCredentials: true });
      loadCompetitionData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start next round');
    }
  };

  const sendAnnouncement = async () => {
    if (!announcementText.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/admin/announce`, { message: announcementText.trim() }, { withCredentials: true });
      setAnnouncements(prev => [...prev, res.data.announcement]);
      setAnnouncementText('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send');
    }
  };

  const quickMessage = (msg) => {
    setAnnouncementText(msg);
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/announcements/${id}`, { withCredentials: true });
      setAnnouncements(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete announcement');
    }
  };

  const clearAllAnnouncements = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/announcements`, { withCredentials: true });
      setAnnouncements([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to clear announcements');
    }
  };

  const resetCompetition = async () => {
    setError('');
    setConfirmAction(null);
    try {
      await axios.post(`${API_URL}/api/admin/reset-competition`, {}, { withCredentials: true });
      setSuccess('Olympiad reset! You can start the competition again.');
      refreshState();
      loadCompetitionData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset');
    }
  };

  const resetRound = async () => {
    setError('');
    setConfirmAction(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/reset-round`, {}, { withCredentials: true });
      setSuccess(`Round ${res.data.round_num} reset — restarting in ${res.data.grace_seconds}s`);
      loadCompetitionData();
      setTimeout(() => setSuccess(''), 3500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset round');
    }
  };

  const restartFromCurrentRound = async () => {
    setError('');
    setConfirmAction(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/restart-from-current-round`, {}, { withCredentials: true });
      setSuccess(`Restarting from Round ${res.data.round_num} — ${res.data.grace_seconds}s grace. Prior rounds preserved.`);
      loadCompetitionData();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to restart from current round');
    }
  };

  const sendReminderEmails = async () => {
    setSendingReminder(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/admin/send-reminder-emails`, {}, { withCredentials: true });
      setSuccess(res.data.message);
      setReminderStatus({ sent: true, count: res.data.sent_count, last_sent: res.data.sent_at });
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingReminder(false);
    }
  };

  if (loading && !competitionStatus) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#2565eb] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Competition
        </h1>
        <p className="text-sm text-[#64748b] mt-1">Setup, registration, and event control — all in one place.</p>
      </div>

      {error && (
        <div className="bg-[#fef2f2] border-2 border-[#fecaca] text-[#dc2626] px-4 py-3 rounded-2xl mb-4 text-sm font-medium flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          <button onClick={() => setError('')} className="ml-auto text-[#dc2626] font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="bg-[#dcfce7] border-2 border-[#86efac] text-[#16a34a] px-4 py-3 rounded-2xl mb-4 text-sm font-medium flex items-center gap-2">
          <CheckCircle className="w-4 h-4 flex-shrink-0" /> {success}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { num: 1, label: 'Setup', unlocked: true },
          { num: 2, label: 'Registration', unlocked: setupComplete },
          { num: 3, label: 'Event Control', unlocked: setupComplete && !regOpen },
        ].map((sec) => (
          <button
            key={sec.num}
            onClick={() => sec.unlocked && setActiveSection(sec.num)}
            disabled={!sec.unlocked}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeSection === sec.num
                ? 'bg-[#2565eb] text-white'
                : sec.unlocked
                  ? 'bg-white text-[#475569] border-2 border-[#e2e8f0] hover:border-[#2565eb]'
                  : 'bg-[#f1f5f9] text-[#cbd5e1] cursor-not-allowed border-2 border-[#e2e8f0]'
            }`}
            data-testid={`competition-section-${sec.num}`}
          >
            {sec.num}. {sec.label}
            {sec.num === 1 && setupComplete && <CheckCircle className="w-4 h-4 inline ml-2 text-[#22c55e]" />}
          </button>
        ))}
      </div>

      {/* Inline Confirmation Bar — shared across all sections */}
      {confirmAction && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in-up"
          data-testid="confirm-action-bar"
          onClick={() => !loading && setConfirmAction(null)}
        >
          <div
            className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border-2 border-[#f59e0b]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <AlertCircle className="w-5 h-5 text-[#f59e0b]" /> Please Confirm
            </h3>
            <p className="text-sm font-medium text-[#475569] mb-5 leading-relaxed">{confirmAction.message}</p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={loading}
                className="px-4 py-2 rounded-xl text-sm font-bold text-[#64748b] hover:bg-[#f1f5f9] transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction.onConfirm}
                disabled={loading}
                className="px-4 py-2 rounded-xl text-sm font-bold bg-[#16a34a] text-white hover:bg-[#15803d] transition-all flex items-center gap-2 disabled:opacity-50"
                data-testid="confirm-action-yes"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Yes, Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section 1: Setup */}
      {activeSection === 1 && (
        <div className="space-y-6 animate-fade-in-up">
        <div className="card-olympiad p-6 md:p-8" data-testid="competition-setup">
          <h2 className="text-lg font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Competition Setup
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Competition Name</label>
              <input
                type="text"
                value={setupForm.name}
                onChange={(e) => setSetupForm(f => ({ ...f, name: e.target.value }))}
                className="input-olympiad"
                placeholder="e.g. Brain Gym Jr. Olympiad 2026"
                data-testid="setup-name"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Venue</label>
              <input
                type="text"
                value={setupForm.venue}
                onChange={(e) => setSetupForm(f => ({ ...f, venue: e.target.value }))}
                className="input-olympiad"
                placeholder="e.g. City Convention Center"
                data-testid="setup-venue"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Event Date</label>
              <input
                type="date"
                value={setupForm.eventDate}
                onChange={(e) => setSetupForm(f => ({ ...f, eventDate: e.target.value }))}
                className="input-olympiad"
                data-testid="setup-date"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Start Time</label>
              <input
                type="time"
                value={setupForm.eventStartTime}
                onChange={(e) => setSetupForm(f => ({ ...f, eventStartTime: e.target.value }))}
                className="input-olympiad"
                data-testid="setup-time"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Seat Limit</label>
              <input
                type="number"
                value={setupForm.seatLimit}
                onChange={(e) => {
                  const v = e.target.value;
                  setSetupForm(f => ({ ...f, seatLimit: v === '' ? '' : (parseInt(v, 10) || 0) }));
                }}
                className="input-olympiad"
                min="1"
                data-testid="setup-seats"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Participation Fee ($)</label>
              <input
                type="number"
                value={setupForm.participationFee}
                onChange={(e) => setSetupForm(f => ({ ...f, participationFee: parseFloat(e.target.value) || 0 }))}
                className="input-olympiad"
                min="0"
                step="0.5"
                data-testid="setup-fee"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#475569] mb-1.5">Age Group <span className="text-[#ef4444]">*</span></label>
              <input
                type="text"
                value={setupForm.ageGroup}
                onChange={(e) => setSetupForm(f => ({ ...f, ageGroup: e.target.value }))}
                className="input-olympiad"
                placeholder="e.g. 6–12, 8–10"
                data-testid="setup-age-group"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={saveSetup}
              disabled={loading || !setupForm.name || !setupForm.eventDate || !setupForm.ageGroup}
              className="btn-secondary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-setup-button"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
              Save Setup
            </button>
            {setupComplete && (
              <span className="text-sm font-bold text-[#22c55e] flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Setup Complete
              </span>
            )}
          </div>
        </div>

        {/* Question Bank Section */}
        <div className="card-olympiad p-6 md:p-8 mt-6 animate-fade-in-up" data-testid="question-bank-section">
          <h2 className="text-lg font-bold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <FolderOpen className="w-5 h-5 text-[#2565eb]" /> Question Bank
          </h2>

          {qbStatus.folders.length === 0 ? (
            <div className="bg-[#f8fafc] rounded-2xl p-5 text-center">
              <p className="text-sm text-[#64748b]">No question bank created yet. Create one in the <span className="font-bold text-[#2565eb]">Questions</span> section.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {qbStatus.folders.map(folder => (
                <div key={folder.id} className="bg-[#f8fafc] rounded-2xl p-5 border border-[#e2e8f0]" data-testid={`qb-folder-${folder.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-[#0f172a] text-sm">{folder.name}</h3>
                        {folder.is_linked ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#dcfce7] text-[#16a34a]">Linked</span>
                        ) : folder.is_ready ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#dcfce7] text-[#16a34a]">Ready</span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#f1f5f9] text-[#64748b]">Incomplete</span>
                        )}
                      </div>
                      <p className="text-xs text-[#64748b]">{folder.finalized_categories} of 8 categories complete</p>
                      <div className="mt-2 w-full max-w-xs bg-[#e2e8f0] rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${folder.is_ready ? 'bg-[#22c55e]' : 'bg-[#2565eb]'}`}
                          style={{ width: `${Math.min(100, (folder.finalized_categories / 8) * 100)}%` }}
                        />
                      </div>
                      {!folder.is_ready && !folder.is_linked && (
                        <p className="text-xs text-[#d97706] mt-2">Finalize all 8 categories to link this question bank.</p>
                      )}
                    </div>
                    <div>
                      {folder.is_linked ? (
                        <button
                          onClick={() => setConfirmAction({ key: 'unlink-qb', message: 'Unlink the question bank from this competition? You can re-link it any time before the event starts.', onConfirm: unlinkQuestionBank })}
                          className="px-4 py-2 rounded-xl text-sm font-bold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#fef2f2] transition-all flex items-center gap-2"
                          data-testid={`unlink-qb-${folder.id}`}
                        >
                          <Unlink className="w-4 h-4" /> Unlink
                        </button>
                      ) : folder.is_ready ? (
                        <button
                          onClick={() => setConfirmAction({ key: 'link-qb', message: 'Link this question bank to the competition? It will be used for all 8 rounds on event day.', onConfirm: () => linkQuestionBank(folder.id) })}
                          className="px-4 py-2 rounded-xl text-sm font-bold bg-[#2565eb] text-white hover:bg-[#1e3a8a] transition-all flex items-center gap-2"
                          data-testid={`link-qb-${folder.id}`}
                        >
                          <Link2 className="w-4 h-4" /> Link to Competition
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger Zone moved out of Section 1 — now rendered as a page-level
            block below so it stays visible regardless of which section pill is
            active (previously buried inside Setup, invisible when the page
            auto-navigated to Registration after Setup Complete). */}
        </div>
      )}

      {/* Section 2: Registration */}
      {activeSection === 2 && (
        <div className="space-y-6 animate-fade-in-up" data-testid="competition-registration">
          <div className="card-olympiad p-6">
            <h2 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Registration Control
            </h2>
            
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#f1f5f9] rounded-2xl p-4 text-center">
                <p className="text-2xl font-bold text-[#0f172a]">{regStats.total}</p>
                <p className="text-xs font-semibold text-[#64748b]">Total Registered</p>
              </div>
              <div className="bg-[#dcfce7] rounded-2xl p-4 text-center">
                <p className="text-2xl font-bold text-[#16a34a]">{regStats.confirmed}</p>
                <p className="text-xs font-semibold text-[#64748b]">Confirmed</p>
              </div>
              <div className="bg-[#fef3c7] rounded-2xl p-4 text-center">
                <p className="text-2xl font-bold text-[#d97706]">{regStats.remaining}</p>
                <p className="text-xs font-semibold text-[#64748b]">Seats Remaining</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {!regOpen ? (
                <button
                  onClick={() => setConfirmAction({ key: 'open-reg', message: regStats.total > 0 ? 'Reopen registration? Parents will be able to register again.' : 'This will open public registration. Parents will be able to register their children.', onConfirm: openRegistration })}
                  disabled={loading}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="open-registration-button"
                >
                  <Play className="w-5 h-5" />
                  {regStats.total > 0 ? 'Reopen Registration' : 'Open Registration'}
                </button>
              ) : (
                <button
                  onClick={() => setConfirmAction({ key: 'close-reg', message: 'This will close registration. No new participants can register after this.', onConfirm: closeRegistration })}
                  disabled={loading}
                  className="px-6 py-3 rounded-full font-bold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#fef2f2] transition-all flex items-center gap-2"
                  data-testid="close-registration-button"
                >
                  Close Registration
                </button>
              )}
            </div>
          </div>

          {/* Quick Adjust: Seats & Time */}
          <div className="card-olympiad p-6">
            <h3 className="text-base font-bold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Clock className="w-5 h-5 text-[#2565eb]" />
              Adjust Seats & Time
            </h3>
            <div className="grid md:grid-cols-2 gap-4 max-w-lg">
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-1.5">Seat Limit</label>
                <input
                  type="number"
                  value={setupForm.seatLimit}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSetupForm(f => ({ ...f, seatLimit: v === '' ? '' : (parseInt(v, 10) || 0) }));
                  }}
                  className="input-olympiad"
                  min="1"
                  data-testid="adjust-seats"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#475569] mb-1.5">Event Start Time</label>
                <input
                  type="time"
                  value={setupForm.eventStartTime}
                  onChange={(e) => setSetupForm(f => ({ ...f, eventStartTime: e.target.value }))}
                  className="input-olympiad"
                  data-testid="adjust-time"
                />
              </div>
            </div>
            <button
              onClick={saveSetup}
              disabled={loading}
              className="mt-4 px-5 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white text-sm flex items-center gap-2 hover:bg-[#1e3a8a] transition-all disabled:opacity-50"
              data-testid="save-adjustments"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save Changes
            </button>
          </div>

          {/* Pre-Event Reminder Email */}
          <div className="card-olympiad p-6">
            <h3 className="text-base font-bold text-[#0f172a] mb-3 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Mail className="w-5 h-5 text-[#2565eb]" />
              Pre-Event Reminder
            </h3>
            <p className="text-sm text-[#64748b] mb-4">
              Send a reminder email to all confirmed participants with event details, codes, and instructions.
            </p>
            {reminderStatus.sent && (
              <div className="bg-[#dcfce7] rounded-xl px-4 py-2 mb-4 text-sm text-[#16a34a] flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Last sent: {new Date(reminderStatus.last_sent).toLocaleString()} ({reminderStatus.count} emails)
              </div>
            )}
            <button
              onClick={sendReminderEmails}
              disabled={sendingReminder}
              className="px-5 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white text-sm flex items-center gap-2 hover:bg-[#1e3a8a] transition-all disabled:opacity-50"
              data-testid="send-reminder-button"
            >
              {sendingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Send Reminder Emails
            </button>
            <p className="text-xs text-[#94a3b8] mt-2">Note: Emails are currently MOCKED (logged to server console).</p>
          </div>
        </div>
      )}

      {/* Section 3: Event Control */}
      {activeSection === 3 && (
        <div className="space-y-6 animate-fade-in-up" data-testid="competition-event-control">
          {/* Shareable Live Leaderboard Link — visible in every state so admin can pre-post it on Facebook */}
          <ShareableLiveLinkCard competition={competition} />
          {/* Start / Lobby Card */}
          <div className="card-olympiad p-6">
            <h2 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {competitionStatus?.status?.startsWith('round_') ? 'Live Competition' : 'Start Competition'}
            </h2>

            {/* Stats row — always visible */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#eff6ff] rounded-2xl p-3 text-center">
                {competitionStatus?.status?.startsWith('round_') ? (
                  <>
                    <p className="text-lg font-bold text-[#16a34a]">Round {competitionStatus.current_round}</p>
                    <p className="text-xs text-[#64748b]">Current</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <Users className="w-5 h-5 text-[#2565eb]" />
                      <p className="text-xl font-bold text-[#0f172a]">{lobbyCount}</p>
                    </div>
                    <p className="text-xs text-[#64748b]">In Lobby</p>
                  </>
                )}
              </div>
              <div className="bg-[#eff6ff] rounded-2xl p-3 text-center">
                <p className="text-lg font-bold text-[#2565eb]">
                  {competitionStatus?.time_remaining_seconds
                    ? `${Math.floor(competitionStatus.time_remaining_seconds / 60)}:${(competitionStatus.time_remaining_seconds % 60).toString().padStart(2, '0')}`
                    : '--:--'}
                </p>
                <p className="text-xs text-[#64748b]">Time Remaining</p>
              </div>
              <div className="bg-[#f1f5f9] rounded-2xl p-3 text-center">
                <p className="text-lg font-bold text-[#0f172a]">{competitionStatus?.participant_count || 0}</p>
                <p className="text-xs text-[#64748b]">Active Participants</p>
              </div>
            </div>

            {/* Paused banner */}
            {competitionStatus?.is_paused && (
              <div className="bg-[#fef3c7] border-2 border-[#f59e0b] rounded-2xl px-4 py-3 mb-4 text-sm font-bold text-[#d97706]">
                Competition is PAUSED
              </div>
            )}

            {/* All control buttons — always visible.
                Backend status vocabulary from /api/competition/status:
                  inactive | lobby | mock_quiz | grace_r1 | grace_reset_X |
                  round_X  | break_X          | finished
                Every value must render *something* so this row is never blank. */}
            <div className="flex flex-wrap gap-3">
              {/* Pre-start states: Start button */}
              {(!competitionStatus ||
                competitionStatus.status === 'inactive' ||
                competitionStatus.status === 'lobby' ||
                competitionStatus.status === 'mock_quiz') && (
                <button
                  onClick={() => setConfirmAction({ key: 'start', message: `This will start the competition for ${lobbyCount} participants in the lobby. This action cannot be undone.`, onConfirm: startCompetition })}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="start-competition-button"
                >
                  <Play className="w-5 h-5" />
                  Start Competition (60s Grace)
                </button>
              )}

              {/* Grace states: pre-round-1 or post-reset grace — show a disabled
                  "Starting…" indicator plus Pause / Reset so the row is never empty
                  and admin can still intervene during the transition. */}
              {(competitionStatus?.status === 'grace_r1' ||
                (typeof competitionStatus?.status === 'string' && competitionStatus.status.startsWith('grace_reset_'))) && (
                <>
                  <button
                    disabled
                    className="px-5 py-2.5 rounded-xl font-bold bg-[#eff6ff] text-[#2565eb] border-2 border-[#bfdbfe] flex items-center gap-2 text-sm cursor-not-allowed opacity-90"
                    data-testid="grace-indicator"
                  >
                    <Timer className="w-4 h-4 animate-pulse" />
                    Starting… Round {competitionStatus.current_round || 1} in {competitionStatus.time_remaining_seconds ?? '—'}s
                  </button>
                  {!competitionStatus.is_paused ? (
                    <button onClick={pauseCompetition} className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#d97706] text-[#d97706] hover:bg-[#fef3c7] transition-all flex items-center gap-2 text-sm" data-testid="pause-btn-grace">
                      <Pause className="w-4 h-4" /> Pause
                    </button>
                  ) : (
                    <button onClick={resumeCompetition} className="px-5 py-2.5 rounded-xl font-bold bg-[#22c55e] text-white hover:bg-[#16a34a] transition-all flex items-center gap-2 text-sm" data-testid="resume-btn-grace">
                      <Play className="w-4 h-4" /> Resume
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmAction({ key: 'reset-round', message: `Reset Round ${competitionStatus.current_round || 1}? This round's scores will be wiped for every participant. A 60-sec grace timer then restarts the round.`, onConfirm: resetRound })}
                    className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#fef2f2] transition-all flex items-center gap-2 text-sm"
                    data-testid="reset-round-btn-grace"
                  >
                    <RotateCcw className="w-4 h-4" /> Reset Round
                  </button>
                </>
              )}

              {competitionStatus?.status?.startsWith('round_') && (
                <>
                  {!competitionStatus.is_paused ? (
                    <button onClick={pauseCompetition} className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#d97706] text-[#d97706] hover:bg-[#fef3c7] transition-all flex items-center gap-2 text-sm" data-testid="pause-btn">
                      <Pause className="w-4 h-4" /> Pause
                    </button>
                  ) : (
                    <button onClick={resumeCompetition} className="px-5 py-2.5 rounded-xl font-bold bg-[#22c55e] text-white hover:bg-[#16a34a] transition-all flex items-center gap-2 text-sm" data-testid="resume-btn">
                      <Play className="w-4 h-4" /> Resume
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmAction({ key: 'reset-round', message: `Reset Round ${competitionStatus.current_round}? This round's scores will be wiped for every participant; earlier rounds stay intact. A 60-sec grace timer then restarts the round.`, onConfirm: resetRound })}
                    className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#fef2f2] transition-all flex items-center gap-2 text-sm"
                    data-testid="reset-round-btn"
                  >
                    <RotateCcw className="w-4 h-4" /> Reset Round
                  </button>
                  <button
                    onClick={() => setConfirmAction({ key: 'restart-from-current', message: `Restart from the start of Round ${competitionStatus.current_round}? Scores from earlier rounds will be PRESERVED. Only the current round restarts after a 30-sec grace. Use this after a hiccup or to give everyone a fresh attempt at this round.`, onConfirm: restartFromCurrentRound })}
                    className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f5f3ff] transition-all flex items-center gap-2 text-sm"
                    data-testid="restart-from-current-btn"
                  >
                    <RefreshCw className="w-4 h-4" /> Restart Round
                  </button>
                </>
              )}

              {competitionStatus?.status?.startsWith('break_') && !competitionStatus.is_paused && (
                <>
                <button
                  onClick={() => setConfirmAction({ key: 'reset-round', message: `Reset Round ${competitionStatus.current_round}? This round's scores will be wiped for every participant; earlier rounds stay intact. A 60-sec grace timer then restarts the round.`, onConfirm: resetRound })}
                  className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#ef4444] text-[#ef4444] hover:bg-[#fef2f2] transition-all flex items-center gap-2 text-sm"
                  data-testid="reset-round-btn-break"
                >
                  <RotateCcw className="w-4 h-4" /> Reset Round {competitionStatus.current_round}
                </button>
                <button
                  onClick={() => setConfirmAction({ key: 'restart-from-current', message: `Restart from Round ${competitionStatus.current_round}? Scores from earlier rounds will be preserved. The current round restarts after a 30-sec grace.`, onConfirm: restartFromCurrentRound })}
                  className="px-5 py-2.5 rounded-xl font-bold border-2 border-[#7c3aed] text-[#7c3aed] hover:bg-[#f5f3ff] transition-all flex items-center gap-2 text-sm"
                  data-testid="restart-from-current-btn-break"
                >
                  <RefreshCw className="w-4 h-4" /> Restart Round
                </button>
                </>
              )}

              {/* Finished state — competition ended. No further live controls;
                  admin can only Reset Olympiad (below) or go to Results / Archive. */}
              {competitionStatus?.status === 'finished' && (
                <div
                  className="px-5 py-2.5 rounded-xl font-bold bg-[#dcfce7] text-[#166534] border-2 border-[#86efac] flex items-center gap-2 text-sm"
                  data-testid="event-ended-indicator"
                >
                  <CheckCircle className="w-4 h-4" />
                  Event Ended · view Results or Archive
                </div>
              )}
            </div>
          </div>

          {/* Reset Olympiad */}
          <div className="card-olympiad p-6 border-2 border-[#fecaca]">
            <h3 className="text-base font-bold text-[#ef4444] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <RotateCcw className="w-5 h-5" /> Reset Olympiad
            </h3>
            <p className="text-sm text-[#64748b] mb-4">
              Resets all competition state — clears lobby, scores, rounds, and paused state. The competition setup (name, date, venue) and registrations are preserved. Use this to start the event day fresh.
            </p>
            <button
              onClick={() => setConfirmAction({ key: 'reset', message: 'Reset the Olympiad? This will clear the lobby, scores, rounds, and paused state. Setup and registrations are preserved.', onConfirm: resetCompetition })}
              className="px-5 py-2.5 rounded-xl font-bold bg-[#ef4444] text-white hover:bg-[#dc2626] transition-all flex items-center gap-2 text-sm"
              data-testid="reset-competition-button"
            >
              <RotateCcw className="w-4 h-4" /> Reset Competition
            </button>
          </div>

          {/* Announcement Panel */}
          <div className="card-olympiad p-6">
            <h3 className="text-base font-bold text-[#0f172a] mb-4 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <Megaphone className="w-5 h-5 text-[#2565eb]" />
              Announcements
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={announcementText}
                onChange={(e) => setAnnouncementText(e.target.value)}
                className="input-olympiad flex-1"
                placeholder="Type a message..."
                maxLength={200}
                data-testid="announcement-input"
              />
              <button
                onClick={sendAnnouncement}
                disabled={!announcementText.trim()}
                className="px-4 py-2 rounded-xl bg-[#2565eb] text-white font-bold text-sm disabled:opacity-50 transition-all hover:bg-[#1e3a8a]"
                data-testid="send-announcement-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            {/* Quick Messages */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['Round starting in 30 seconds', 'Please wait', 'Technical issue — we will resume shortly.'].map(msg => (
                <button
                  key={msg}
                  onClick={() => quickMessage(msg)}
                  className="px-3 py-1.5 rounded-lg bg-[#f1f5f9] text-xs font-medium text-[#475569] hover:bg-[#e2e8f0] transition-colors"
                  data-testid={`quick-msg-${msg.slice(0, 10)}`}
                >
                  {msg}
                </button>
              ))}
            </div>
            {/* History */}
            {announcements.length > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">Sent ({announcements.length})</p>
                  <button
                    onClick={clearAllAnnouncements}
                    className="text-xs font-semibold text-[#dc2626] hover:text-[#991b1b] flex items-center gap-1"
                    data-testid="clear-all-announcements-btn"
                  >
                    <Trash2 className="w-3 h-3" /> Clear All
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {announcements.slice().reverse().map((a) => (
                    <div key={a.id} className="bg-[#fef3c7] rounded-xl px-3 py-2 text-sm text-[#92400e] flex items-start gap-2 group" data-testid={`announcement-item-${a.id}`}>
                      <Megaphone className="w-3 h-3 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p>{a.message}</p>
                        <p className="text-xs text-[#d97706] mt-0.5">{new Date(a.sent_at).toLocaleTimeString()}</p>
                      </div>
                      <button
                        onClick={() => deleteAnnouncement(a.id)}
                        className="text-[#92400e] hover:text-[#dc2626] opacity-60 hover:opacity-100 transition-opacity flex-shrink-0 p-1 -m-1"
                        title="Delete — removes from participants' screens"
                        aria-label="Delete announcement"
                        data-testid={`delete-announcement-${a.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Danger Zone — always visible when a competition exists (setupComplete),
          across every section pill. Previously it was gated inside Section 1
          which auto-hid after Setup was saved, making Delete Competition
          impossible to find from Registration/Event Control sections. */}
      {setupComplete && competitionStatus?.status !== 'round_1' && competitionStatus?.status !== 'round_2' &&
        competitionStatus?.status !== 'round_3' && competitionStatus?.status !== 'round_4' &&
        competitionStatus?.status !== 'round_5' && competitionStatus?.status !== 'round_6' &&
        competitionStatus?.status !== 'round_7' && competitionStatus?.status !== 'round_8' && (
        <div className="card-olympiad p-6 md:p-8 mt-8 border-2 border-[#fecaca] bg-[#fff7f7]" data-testid="danger-zone">
          <h2 className="text-lg font-bold text-[#dc2626] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            <AlertCircle className="w-5 h-5" /> Danger Zone
          </h2>
          <p className="text-sm text-[#7f1d1d] mb-4">
            Delete this competition. The competition will be <span className="font-bold">soft-archived</span> — the question bank is preserved
            and full participant records (with rankings) are kept inside the Archive page.
            The active Participants page, live runtime data (lobby, leaderboard, announcements, event-specific access codes) will be cleared, and you&apos;ll be free to create a new competition.
          </p>
          <button
            onClick={() => setConfirmAction({
              key: 'delete-comp',
              message: `Archive "${setupForm.name || 'this competition'}"? Final rankings are preserved in the Archive page. This cannot be undone.`,
              onConfirm: deleteCompetition,
            })}
            disabled={loading}
            className="px-5 py-2.5 rounded-xl font-bold bg-[#dc2626] text-white hover:bg-[#b91c1c] transition-all flex items-center gap-2 disabled:opacity-50"
            data-testid="delete-competition-button"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete Competition
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminCompetitionPage;
